import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import { API } from "@/App";
import {
  LogOut, Phone, Mail, MapPin, ImageIcon, Loader2, RefreshCw, Trash2, X, Lock
} from "lucide-react";

const STATUS_OPTIONS = ["new", "contacted", "scheduled", "completed", "archived"];

const STATUS_COLORS = {
  new: "bg-[#0A0A0A] text-white",
  contacted: "bg-[#FFFFFF] text-black",
  scheduled: "bg-blue-500 text-white",
  completed: "bg-green-600 text-white",
  archived: "bg-neutral-400 text-black",
};

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString();
  } catch {
    return iso;
  }
}

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem("nji_admin_token") || "");
  const [password, setPassword] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [quotes, setQuotes] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState("all");

  const authHeaders = { headers: { "X-Admin-Password": token } };

  const login = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      const tok = res.data.token;
      localStorage.setItem("nji_admin_token", tok);
      setToken(tok);
      toast.success("Signed in");
      setPassword("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid password");
    } finally {
      setLoginLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("nji_admin_token");
    setToken("");
    setQuotes([]);
    setStats(null);
  };

  const loadData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [q, s] = await Promise.all([
        axios.get(`${API}/admin/quotes`, authHeaders),
        axios.get(`${API}/admin/stats`, authHeaders),
      ]);
      setQuotes(q.data);
      setStats(s.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Session expired. Sign in again.");
        logout();
      } else {
        toast.error("Failed to load data");
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { loadData(); }, [loadData]);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/quotes/${id}`, { status }, authHeaders);
      toast.success("Status updated");
      loadData();
      if (selected?.id === id) setSelected({ ...selected, status });
    } catch {
      toast.error("Failed to update");
    }
  };

  const deleteQuote = async (id) => {
    if (!window.confirm("Delete this quote request?")) return;
    try {
      await axios.delete(`${API}/admin/quotes/${id}`, authHeaders);
      toast.success("Deleted");
      setSelected(null);
      loadData();
    } catch {
      toast.error("Failed to delete");
    }
  };

  // ---------------- LOGIN VIEW ----------------
  if (!token) {
    return (
      <div>
        <Navbar />
        <div className="min-h-[80vh] grid place-items-center px-4 py-16 bg-[#F7F5F1]">
          <form
            onSubmit={login}
            data-testid="admin-login-form"
            className="w-full max-w-md border-[3px] border-black bg-white p-8 shadow-brutal-lg"
          >
            <div className="flex h-12 w-12 items-center justify-center border-[3px] border-black bg-[#0A0A0A] mb-5 shadow-brutal-sm">
              <Lock className="h-6 w-6 text-white" />
            </div>
            <h1 className="font-display uppercase text-3xl mb-1">Admin Access</h1>
            <p className="text-neutral-600 text-sm mb-6">Enter your password to view quote requests.</p>
            <label className="block text-xs font-bold uppercase tracking-widest mb-2">Password</label>
            <input
              data-testid="admin-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full border-[3px] border-black bg-white px-4 py-3 font-medium focus:outline-none focus:border-[#0A0A0A]"
              placeholder="••••••••"
              required
            />
            <button
              disabled={loginLoading}
              type="submit"
              data-testid="admin-login-btn"
              className="mt-5 w-full flex items-center justify-center gap-2 border-[3px] border-black bg-black text-white px-6 py-3 font-bold uppercase tracking-wider shadow-brutal btn-brutal-hover disabled:opacity-70"
            >
              {loginLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Sign In
            </button>
          </form>
        </div>
      </div>
    );
  }

  // ---------------- DASHBOARD ----------------
  const filtered = filter === "all" ? quotes : quotes.filter((q) => q.status === filter);

  return (
    <div className="min-h-screen bg-[#F7F5F1]">
      <Navbar />
      <div className="mx-auto max-w-7xl px-4 md:px-8 py-8">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.3em] text-[#0A0A0A] mb-1">— Control room —</div>
            <h1 data-testid="admin-title" className="font-display uppercase text-3xl md:text-5xl">Quote Requests</h1>
          </div>
          <div className="flex gap-2">
            <button
              onClick={loadData}
              data-testid="admin-refresh-btn"
              className="border-[3px] border-black bg-white px-4 py-2 font-bold uppercase text-sm tracking-widest shadow-brutal-sm btn-brutal-hover flex items-center gap-2"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout-btn"
              className="border-[3px] border-black bg-black text-white px-4 py-2 font-bold uppercase text-sm tracking-widest shadow-brutal-sm btn-brutal-hover flex items-center gap-2"
            >
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div data-testid="admin-stats" className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8">
            {[
              { label: "Total", key: "total", color: "bg-white" },
              { label: "New", key: "new", color: "bg-[#0A0A0A] text-white" },
              { label: "Contacted", key: "contacted", color: "bg-[#FFFFFF]" },
              { label: "Scheduled", key: "scheduled", color: "bg-blue-500 text-white" },
              { label: "Completed", key: "completed", color: "bg-green-600 text-white" },
            ].map((s) => (
              <div key={s.key} className={`border-[3px] border-black p-4 shadow-brutal-sm ${s.color}`}>
                <div className="text-xs font-bold uppercase tracking-widest">{s.label}</div>
                <div className="font-display text-3xl mt-1">{stats[s.key] ?? 0}</div>
              </div>
            ))}
          </div>
        )}

        {/* Filter */}
        <div className="flex flex-wrap gap-2 mb-4">
          {["all", ...STATUS_OPTIONS].map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              data-testid={`filter-${s}`}
              className={`border-[2px] border-black px-3 py-1.5 text-xs font-bold uppercase tracking-widest ${
                filter === s ? "bg-black text-white" : "bg-white text-black"
              }`}
            >
              {s}
            </button>
          ))}
        </div>

        {/* Table */}
        <div className="border-[3px] border-black bg-white shadow-brutal overflow-hidden">
          {loading ? (
            <div className="p-16 text-center">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-[#0A0A0A]" />
            </div>
          ) : filtered.length === 0 ? (
            <div data-testid="empty-state" className="p-16 text-center text-neutral-500">
              <p className="font-display uppercase text-2xl mb-2">No requests yet</p>
              <p className="text-sm">Submitted quotes will appear here.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-black text-white uppercase tracking-widest text-xs">
                  <tr>
                    <th className="p-3 text-left">Received</th>
                    <th className="p-3 text-left">Name</th>
                    <th className="p-3 text-left">Phone</th>
                    <th className="p-3 text-left">Service</th>
                    <th className="p-3 text-left">Photos</th>
                    <th className="p-3 text-left">Status</th>
                    <th className="p-3 text-left">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((q) => (
                    <tr key={q.id} data-testid={`quote-row-${q.id}`} className="border-t-2 border-black hover:bg-[#FFFFFF]/20">
                      <td className="p-3 whitespace-nowrap text-neutral-600">{formatDate(q.created_at)}</td>
                      <td className="p-3 font-semibold">{q.name}</td>
                      <td className="p-3">{q.phone}</td>
                      <td className="p-3">{q.service_type}</td>
                      <td className="p-3">{q.photos?.length || 0}</td>
                      <td className="p-3">
                        <span className={`inline-block border-[2px] border-black px-2 py-1 text-[10px] font-bold uppercase tracking-widest ${STATUS_COLORS[q.status] || "bg-white"}`}>
                          {q.status}
                        </span>
                      </td>
                      <td className="p-3">
                        <button
                          onClick={() => setSelected(q)}
                          data-testid={`view-quote-${q.id}`}
                          className="border-[2px] border-black bg-white px-3 py-1 text-xs font-bold uppercase tracking-widest hover:bg-[#0A0A0A] hover:text-white"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Detail Modal */}
      {selected && (
        <div
          data-testid="quote-detail-modal"
          className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-4 overflow-y-auto"
          onClick={() => setSelected(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white border-[3px] border-black w-full max-w-3xl shadow-brutal-lg my-8"
          >
            <div className="flex items-center justify-between border-b-[3px] border-black p-4">
              <div>
                <div className="text-xs uppercase tracking-widest text-neutral-500">Quote request</div>
                <h3 className="font-display uppercase text-2xl">{selected.name}</h3>
              </div>
              <button
                onClick={() => setSelected(null)}
                data-testid="modal-close"
                className="border-[3px] border-black p-2 shadow-brutal-sm bg-white"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid gap-3 md:grid-cols-2">
                <a href={`tel:${selected.phone}`} className="flex items-center gap-2 border-[3px] border-black bg-[#0A0A0A] text-white p-3 shadow-brutal-sm">
                  <Phone className="h-4 w-4" />
                  <span className="font-bold">{selected.phone}</span>
                </a>
                {selected.email && (
                  <a href={`mailto:${selected.email}`} className="flex items-center gap-2 border-[3px] border-black bg-black text-white p-3 shadow-brutal-sm break-all">
                    <Mail className="h-4 w-4 shrink-0" />
                    <span className="font-bold text-sm">{selected.email}</span>
                  </a>
                )}
              </div>
              {selected.address && (
                <div className="flex items-start gap-2 text-sm">
                  <MapPin className="h-4 w-4 mt-0.5 text-[#0A0A0A]" />
                  <span>{selected.address}</span>
                </div>
              )}
              <div>
                <div className="text-xs uppercase tracking-widest font-bold text-neutral-500 mb-1">Service</div>
                <div className="font-semibold">{selected.service_type}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest font-bold text-neutral-500 mb-1">Description</div>
                <p className="whitespace-pre-wrap text-neutral-800">{selected.description}</p>
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest font-bold text-neutral-500 mb-2 flex items-center gap-2">
                  <ImageIcon className="h-4 w-4" /> Photos ({selected.photos?.length || 0})
                </div>
                {selected.photos?.length ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {selected.photos.map((p, i) => (
                      <a key={i} href={p} target="_blank" rel="noreferrer" className="border-[3px] border-black block shadow-brutal-sm">
                        <img src={p} alt={`photo-${i}`} className="w-full h-32 object-cover" />
                      </a>
                    ))}
                  </div>
                ) : (
                  <div className="text-sm text-neutral-500">No photos attached.</div>
                )}
              </div>
              <div className="border-t-[3px] border-black pt-4">
                <div className="text-xs uppercase tracking-widest font-bold text-neutral-500 mb-2">Update status</div>
                <div className="flex flex-wrap gap-2">
                  {STATUS_OPTIONS.map((s) => (
                    <button
                      key={s}
                      onClick={() => updateStatus(selected.id, s)}
                      data-testid={`set-status-${s}`}
                      className={`border-[2px] border-black px-3 py-1.5 text-xs font-bold uppercase tracking-widest ${
                        selected.status === s ? STATUS_COLORS[s] : "bg-white"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="border-t-[3px] border-black p-4 flex justify-between">
              <button
                onClick={() => deleteQuote(selected.id)}
                data-testid="delete-quote-btn"
                className="flex items-center gap-2 border-[3px] border-black bg-white px-4 py-2 text-sm font-bold uppercase tracking-widest text-red-600 shadow-brutal-sm btn-brutal-hover"
              >
                <Trash2 className="h-4 w-4" /> Delete
              </button>
              <button
                onClick={() => setSelected(null)}
                className="border-[3px] border-black bg-black text-white px-4 py-2 text-sm font-bold uppercase tracking-widest shadow-brutal-sm btn-brutal-hover"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
